import { createSignal } from 'solid-js'

export type ShareOverlaySource = 'app' | 'account' | null

type ShareOverlayState = {
   isOpen: boolean
   title: string
   description: string
   url: string
   source: ShareOverlaySource
}

const [shareOverlayState, setShareOverlayState] = createSignal<ShareOverlayState>({
   isOpen: false,
   title: '',
   description: '',
   url: '',
   source: null,
})

/**
 * Opens the global share overlay with the given content.
 *
 * @param next - The overlay title, description, url, and optional source.
 */
export function openShareOverlay(next: {
   title: string
   description: string
   url: string
   source?: ShareOverlaySource
}) {
   setShareOverlayState({
      isOpen: true,
      title: next.title,
      description: next.description,
      url: next.url,
      source: next.source ?? null,
   })
}

/**
 * Opens the share overlay for a URL, falling back to the app share link and
 * default copy when the caller leaves the title, description, or url blank.
 *
 * @param appShareBaseUrl - The public app URL used as the fallback link.
 * @param options - The requested title, description, and url.
 * @param translate - The translation function for the default copy.
 */
export function openShareOverlayWithDefaults(
   appShareBaseUrl: string,
   options: { title: string; description: string; url: string },
   translate: (key: string) => string,
) {
   const targetUrl = options.url || appShareBaseUrl
   const title = options.title.trim()
   openShareOverlay({
      title: title.length ? title : translate('Share Faster · Leaner · Stronger'),
      description:
         options.description.trim().length ?
            options.description
         :  translate('Scan this QR code or open the link below on another device.'),
      url: targetUrl,
      source: targetUrl === appShareBaseUrl ? 'app' : 'account',
   })
}

/**
 * Closes and clears the global share overlay.
 */
export function closeShareOverlay() {
   setShareOverlayState({
      isOpen: false,
      title: '',
      description: '',
      url: '',
      source: null,
   })
}

/**
 * Builds a QR-code image URL for a share link.
 *
 * @param url - The URL to encode.
 * @returns The QR image URL.
 */
export function getShareQrImageUrl(url: string) {
   return `https://quickchart.io/qr?size=320&margin=2&text=${encodeURIComponent(url)}`
}

export { shareOverlayState }
